import { useCallback, useContext, useEffect, useLayoutEffect, useMemo } from 'react';
import type { IFieldElem } from '@models/IFormData';
import type { IChangeAction, IDocumentTable, IChanges } from '@models/Forms/IForms';
import type { UseFormReturn } from 'react-hook-form';
import { ChangesFieldValue, type SaveChangeField } from '@utils/managers/ChangesManager';
import {
    ChangeRuntime,
    isDocumentFieldKey,
    type ChangeHandler,
    type IChangeFieldBinding,
    type IChangeInvocation,
} from '@utils/managers/ChangeRuntime';
import { sendModalMsg } from '@srm/ui/lib/modalMessages';
import { RowFormOperationsContext } from '@organisms/TableData/ModalForms/RowFormOperationsContext';
import { useLatest } from '@srm/ui/hooks/useLatest';
import { ChangeHandlersContext } from './ChangeHandlersContext';
import { isNestedChangeInvocation, reportChangeError } from './changeErrors';

export const useChangeHandlersOptional = () => useContext(ChangeHandlersContext);

/** Объявления, ошибки реестра которых уже показаны: форма строки с customForm собирает реестр
 *  заново при каждом открытии из той же схемы (кэш на сессию), StrictMode — дважды на маунт. */
const reportedRegistries = new WeakSet<IChangeAction[]>();

/**
 * Реестр changeActions формы. Невалидные объявления отбрасываются и показываются
 * пользователю, а не роняют рендер карточки.
 */
export const useChangeRuntime = (actions: IChangeAction[] | undefined) => {
    const runtime = useMemo(() => new ChangeRuntime(actions ?? []), [actions]);
    useEffect(() => {
        if (!runtime.errors.length || !actions || reportedRegistries.has(actions)) return;
        reportedRegistries.add(actions);
        sendModalMsg({ message: [...runtime.errors], type: 'error' });
    }, [runtime, actions]);
    return runtime;
};

export const useRegisterChangeHandler = (binding: IChangeFieldBinding, handler: ChangeHandler, enabled = true) => {
    const context = useChangeHandlersOptional();
    // Обработчик в ref: перерегистрация только на смену привязки/реестра, а не на каждый рендер.
    const handlerRef = useLatest(handler);
    const { key, tableKey, rowId, scopeId } = binding;
    const runtime = context?.runtime;
    useLayoutEffect(() => {
        if (!enabled || !runtime) return;
        const registered = { key, tableKey, rowId, scopeId };
        const handleChange: ChangeHandler = (request) => handlerRef.current(request);
        runtime.register(registered, handleChange);
        return () => runtime.unregister(registered, handleChange);
    }, [runtime, enabled, key, tableKey, rowId, scopeId, handlerRef]);
};

/**
 * Показ отказа каскада в корне вызова (пользовательское событие, стартовый каскад контрола).
 * В форме строки отказ показывает TableForm при применении (operations.flush), здесь он только
 * фиксируется явно: подформа внутри формы строки трекером формы строки не охвачена.
 */
export const useReportChangeError = () => {
    const operations = useContext(RowFormOperationsContext);
    return useCallback(
        (error: unknown, setError: ((errors?: string[]) => void) | undefined) => {
            console.error('Ошибка выполнения changes', error);
            if (operations) {
                operations.track(() => Promise.reject(error)).catch(() => undefined);
                return;
            }
            reportChangeError(error, setError);
        },
        [operations],
    );
};

export const useRunFieldChanges = () => {
    const context = useChangeHandlersOptional();
    const reportError = useReportChangeError();
    return useCallback(
        async (
            changes: IChanges,
            value: any,
            fieldName: string,
            data: Record<string, IFieldElem>,
            isEdit: boolean,
            isNew: boolean,
            onSave: SaveChangeField,
            setError: (errors?: string[]) => void,
            table: IDocumentTable | undefined,
            rowData: any,
            formMethods: UseFormReturn<any>,
            binding: IChangeFieldBinding,
            rowOldData?: any,
            invocation: IChangeInvocation = { activeTargets: new Set() },
        ) => {
            const isActive = () => invocation.isCurrent?.() !== false;
            if (!isActive()) return;
            try {
                await ChangesFieldValue(
                    changes,
                    value,
                    fieldName,
                    data,
                    isEdit,
                    isNew,
                    (...args) => isActive() ? onSave(...args) : { saved: false, value: undefined },
                    (errors) => { if (isActive()) setError(errors); },
                    table,
                    rowData,
                    invocation.isCurrent ? { ...formMethods, setValue: (...args) => {
                        if (isActive()) formMethods.setValue(...args);
                    } } : formMethods,
                    rowOldData,
                    context
                        ? (target, savedValue, savedRow, activeTargets, savedOldRow) => {
                              const resolvedTarget = {
                                  ...target,
                                  scopeId: isDocumentFieldKey(target.key)
                                      ? undefined
                                      : (target.scopeId ?? invocation.scopeId ?? binding.scopeId),
                              };
                              return context.runSavedChanges(resolvedTarget, savedValue, savedRow, savedOldRow, {
                                  isCurrent: invocation.isCurrent,
                                  activeTargets: activeTargets ?? invocation.activeTargets,
                                  scopeId: invocation.scopeId ?? binding.scopeId,
                              });
                          }
                        : undefined,
                    invocation.activeTargets,
                    context?.runtime.actions,
                );
            } catch (error) {
                // callAction намеренно бросает (неизвестное действие, null в параметре).
                // Цель invokeChanges пробрасывает: каскад-источник обрывается, ошибку покажет корень.
                if (isNestedChangeInvocation(invocation)) throw error;
                if (isActive()) reportError(error, setError);
            }
        },
        [context, reportError],
    );
};

export const useRegisterFieldChanges = (
    binding: IChangeFieldBinding,
    changes: IChanges,
    fieldName: string,
    data: Record<string, IFieldElem>,
    isEdit: boolean,
    isNew: boolean,
    onSave: SaveChangeField,
    setError: (errors?: string[]) => void,
    formMethods: UseFormReturn<any>,
    table?: IDocumentTable,
    enabled = true,
) => {
    const runChanges = useRunFieldChanges();
    useRegisterChangeHandler(
        binding,
        ({ value, invocation, rowData, rowOldData }) =>
            runChanges(
                changes,
                value,
                fieldName,
                data,
                isEdit,
                isNew,
                onSave,
                setError,
                table,
                rowData,
                formMethods,
                binding,
                rowOldData,
                invocation,
            ),
        enabled,
    );
};
